<script lang="ts" setup>
import { computed } from 'vue'
import type { RunHistory } from '@/types'
import RunCostRows from './RunCostRows.vue'
import { formatDuration } from '../utils/runFormatters'

const props = defineProps<{ show: boolean; run: RunHistory | null }>()
const emit = defineEmits<{ (e: 'update:show', value: boolean): void }>()

// A service that recorded no cost is one that has not completed yet — its own document holds what it has
// spent so far, and the run only takes it at completion. Shown rather than hidden, so a running service
// reads as "not yet" instead of vanishing from a list the reader is counting through.
const services = computed(() => props.run?.services ?? [])

function close() {
  emit('update:show', false)
}
</script>

<template>
  <div v-if="show && run" class="modal fade show d-block" tabindex="-1" @click="close">
    <div class="modal-dialog modal-lg modal-dialog-scrollable" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">Stats for nerds</h2>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <div class="modal-body">
          <h3 class="h6 text-uppercase text-muted mb-3">This run</h3>
          <RunCostRows
            :cost="run.cost"
            :queue-wait-ms="run.queueWaitMs"
            :processing-ms="run.durationInMilliSeconds"
          />

          <h3 class="h6 text-uppercase text-muted mt-4 mb-3">By service</h3>
          <div class="d-flex flex-column gap-3">
            <div v-for="service in services" :key="service.serviceId">
              <div class="d-flex align-items-baseline gap-2 mb-1">
                <span class="fw-semibold small">{{ service.serviceName }}</span>
                <small class="text-muted">{{ formatDuration(service.processingMs) }}</small>
                <small v-if="service.achievedConcurrency" class="text-muted">
                  &middot; {{ service.achievedConcurrency.toFixed(1) }} in flight on average
                </small>
              </div>
              <RunCostRows :cost="service.cost" :processing-ms="service.processingMs" />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="bb-btn-secondary-cta" @click="close">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>
